export const ContentfulTypes = {
  PRODUCT_TEST_KIT: 'productTestKit',
  SHOP_PAGE_TEST: 'shopTest',
  PRODUCT_FORMULA: 'productFormula',
  SHOP_PAGE_FORMULAS: 'shopFormula',
} as const;

export type ContentfulTypeId =
  (typeof ContentfulTypes)[keyof typeof ContentfulTypes];

export interface ProductTestKit {
  testId: string;
  title: string;
  description?: string;
  productImageUrl?: string;
  productMessage?: string;
  promo?: Promo;
  cta?: TestCTA;
  couponCode?: string;
  discountPercentage?: number;
  discountAmount?: number;
}

export type PromoSplitType = 'COUNTDOWN' | 'TEXT';

export interface Promo {
  splitOne: string;
  splitTwo: string;
}

export interface TestCTA {
  ctaText: string;
  ctaColor: string;
}

export interface ShopTest {
  id: string;
  title?: string;
  tests?: ProductTestKit[]; // Array of linked ProductTestKit entries
}

export interface FormulaIncludes {
  title?: string;
  imageUrl?: string;
}

export interface FormulaProfile {
  sectionTitle: string;
  sectionListItems: string[] | [];
  sectionDescription: string;
}

export interface ProductFormulas {
  title: string;
  productId: string;
  shortDescription?: string;
  longDescription?: string;
  productImageUrl?: string;
  promoVideoUrl?: string;
  formulaShortDescription?: string;
  formulaBenefits?: string[];
  formulaProfile?: FormulaProfile;
  formulaIncludes?: FormulaIncludes[];
  disclaimer?: string;
  productReferences?: string[];
  color?: string;
}

export interface ShopFormula {
  tabName?: string;
  title?: Record<string, string>;
  subtitle?: Record<string, string>;
  promo?: Promo;
  products?: ProductFormulas[];
  couponCode?: string;
  offerDuration?: string;
  discountPercentage?: number;
}

// Contentful API response types
export interface ContentfulSys {
  id: string;
  type: string;
  linkType?: string;
}

export interface ContentfulAsset {
  sys: ContentfulSys;
  fields: {
    title?: string;
    description?: string;
    file: {
      url: string;
      details?: {
        size?: number;
        image?: {
          width: number;
          height: number;
        };
      };
      fileName?: string;
      contentType?: string;
    };
  };
}

export interface ContentfulImage {
  sys: {
    id: string;
    type: string;
  };
}

export interface ContentfulIncludes {
  Asset?: ContentfulAsset[];
  Entry?: ContentfulEntry[];
}

export interface ContentfulEntry {
  sys: ContentfulSys;
  fields: Record<string, unknown>;
}

export interface ContentfulProductTestKitEntry extends ContentfulEntry {
  fields: {
    testId: string;
    title: string;
    description?: string;
    productMessage?: string;
    promo?: Promo;
    cta?: TestCTA;
    productImage?: ContentfulImage;
    couponCode?: string;
    discountPercentage?: number;
    discountAmount?: number;
  };
}

export interface ContentfulProductFormulaEntry extends ContentfulEntry {
  fields: {
    title: string;
    productId: string;
    shortDescription?: string;
    longDescription?: string;
    promoVideoUrl?: string;
    formulaShortDescription?: string;
    formulaProfile?: FormulaProfile;
    disclaimer?: string;
    formulaBenefits?: string[];
    productReferences?: string[];
    color?: string;
    productImage?: ContentfulImage;
    formulaIncludes?: FormulaIncludes[];
  };
}

export interface ContentfulLink {
  sys: {
    id: string;
    type: string; // Can be "Link" or other types
    linkType?: 'Entry' | 'Asset';
  };
}

export interface ContentfulShopTestEntry extends ContentfulEntry {
  fields: {
    title?: string;
    tests?: ContentfulLink[];
  };
}

export interface ContentfulShopFormulaEntry extends ContentfulEntry {
  fields: {
    tabName?: string;
    title?: Record<string, string>;
    subtitle?: Record<string, string>;
    promo?: Promo;
    products?: ContentfulLink[];
    couponCode?: string;
    offerDuration?: string;
    discountPercentage?: number;
  };
}

export interface ContentfulResponse<T extends ContentfulEntry> {
  items: T[];
  includes: ContentfulIncludes;
  total: number;
  limit: number;
  skip: number;
}
